import { ActionButtonsContainer, Button, ContentSection } from '@vwfs-bronson/bronson-react';
import { ContinueLater } from '@vwfs-its/sf-sac-frontend';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { appConfig } from '../../../config';
import { brandNames } from '../../../shared/common';
import { useApplicationState } from '../../App.state';
import { countryCodes } from '../../models';
import { saveAndContinueLater } from '../../api/saveAndContinue.service';
import { DataStorageData } from '../../../shared/types';
import { getResumePathWithoutToken, getSaveForLaterStepFromPath } from '../../utils/saveForLater';
import { buildSubmitAnswersRequestBody } from '../../utils/submitAnswersRequest';
import { Step, SubmitAnswersRequestBody } from '../../../shared/interface';
import { getSaveForLaterDraft } from '../../utils/saveForLaterDraft';
import { queryClient } from '../../queryClient';
import { transactionDataQueryKey } from '../../api/transactionQueryKeys';

export function ContinueLaterPopup() {
  const { state } = useApplicationState();

  const location = useLocation();

  const [showContinueLater, setShowContinueLater] = useState(false);
  const { t } = useTranslation('sf-sac');

  const getCurrentLocationHref = (): string =>
    `${location.pathname}${location.search}${location.hash}?transactionId=${state.transactionId}&LegalEntity=${state.legalEntity}&DealerCompanyNumber=${state.preSelectedDealerId}`;

  const constructRedirectUrl = (): string => {
    return getResumePathWithoutToken(getCurrentLocationHref()).replace(/#.*$/, '');
  };

  const onContinueLater = async (values: any) => {
    const dataStorageData = queryClient.getQueryData<DataStorageData>(
      transactionDataQueryKey(state.transactionId)
    );
    const transactionStatus = dataStorageData?.transaction?.status ?? state.transactionStatus;
    const { continueLaterPhone, continueLaterEmail } = values;

    const currentStep = getSaveForLaterStepFromPath(location.pathname);

    const { matchMakerAnswers, customerInformation } = getSaveForLaterDraft(state);
    const saveForLaterRequestBody: SubmitAnswersRequestBody = {
      ...buildSubmitAnswersRequestBody({
        state,
        currentStep: currentStep ?? Step.MM1,
        matchMakerAnswers,
        contactDetails: {
          ...customerInformation,
          email: continueLaterEmail,
          phoneNumber: continueLaterPhone
        },
        selectedDealer: state.dealer?.id || state.preSelectedDealerId || undefined
      }),
      custom: {
        url: constructRedirectUrl(),
        skip: state.skip,
        currentStep: currentStep ?? Step.MM1
      }
    };

    await saveAndContinueLater(saveForLaterRequestBody, state.transactionId, transactionStatus);
  };

  return (
    <>
      <ActionButtonsContainer>
        <Button
          incognito
          action
          icon="semantic-save-local"
          testId="continueLaterFloatingButton"
          onClick={() => {
            setShowContinueLater(true);
          }}
        >
          {t('tooltip')}
        </Button>
      </ActionButtonsContainer>

      <ContentSection.ComponentWrapper>
        <ContinueLater
          version={1}
          disableMandatoryAsterisk
          productName={brandNames[appConfig.brandName]}
          dataProtection={true}
          testId="continueLaterModal"
          showModal={{
            showContinueLater,
            setShowContinueLater
          }}
          email={state.customerInformation.email}
          phoneNo={state.customerInformation.phoneNumber}
          phoneCountryCode={'+49'}
          dataModels={{
            customerCountryCodeList: countryCodes
          }}
          onCancel={() => {
            setShowContinueLater(false);
          }}
          onSendAgainSubmit={(values: any) => {
            onContinueLater(values);
          }}
          linkValidityText={t('sf-sac:linkValidity')}
          onContinueLaterSubmit={(values: any) => {
            onContinueLater(values);
            setShowContinueLater(false);
          }}
          hideDataProtectionCheckbox={true}
        />
      </ContentSection.ComponentWrapper>
    </>
  );
}
